import React, { useState, useEffect } from 'react';
import { Users, Eye, BookOpen, DollarSign, TrendingUp, Activity } from 'lucide-react';
import { getAdminStats } from '../../services/api';

export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await getAdminStats();
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-[var(--surface)] rounded-xl p-6 animate-pulse">
            <div className="h-4 w-20 bg-[var(--border)] rounded mb-4" />
            <div className="h-8 w-16 bg-[var(--border)] rounded" />
          </div>
        ))}
      </div>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-blue-500' },
    { label: 'Total Creators', value: stats?.total_creators || 0, icon: TrendingUp, color: 'text-green-500' },
    { label: 'Total Comics', value: stats?.total_comics || 0, icon: BookOpen, color: 'text-purple-500' },
    { label: 'Revenue (MTD)', value: `$${(stats?.revenue_mtd || 0).toFixed(2)}`, icon: DollarSign, color: 'text-yellow-500' },
    { label: 'Active Subscriptions', value: stats?.active_subscriptions || 0, icon: Activity, color: 'text-[var(--primary)]' },
    { label: 'Page Views (Today)', value: stats?.page_views_today || 0, icon: Eye, color: 'text-cyan-500' },
    { label: 'New Users (7d)', value: stats?.new_users_week || 0, icon: Users, color: 'text-indigo-500' },
    { label: 'Pending Feedback', value: stats?.pending_feedback || 0, icon: Activity, color: 'text-orange-500' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h2 className="text-2xl font-teko">Dashboard Overview</h2>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div 
              key={i} 
              className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 hover:border-[var(--primary)]/30 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[var(--text-secondary)]">{stat.label}</span>
                <Icon size={18} className={stat.color} />
              </div>
              <p className="text-2xl font-teko font-semibold">{stat.value}</p>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
        <h3 className="text-xl font-teko mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickAction label="Add Comic" onClick={() => {}} />
          <QuickAction label="Send Announcement" onClick={() => {}} />
          <QuickAction label="Review Feedback" onClick={() => {}} />
          <QuickAction label="Export Data" onClick={() => {}} />
        </div>
      </div>

      {/* Recent Activity Preview */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
        <h3 className="text-xl font-teko mb-4">Recent Activity</h3>
        <p className="text-[var(--text-secondary)] text-sm">
          View full activity log in the Activity section.
        </p>
      </div>
    </div>
  );
}

function QuickAction({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="p-4 bg-[var(--background)] rounded-lg text-center hover:bg-[var(--primary)]/10 transition-colors"
    >
      {label}
    </button>
  );
}

export default AdminDashboard;
